import AttachFileIcon from '@mui/icons-material/AttachFile';
import EventIcon from '@mui/icons-material/Event';
import HistoryIcon from '@mui/icons-material/History';
import LayersIcon from '@mui/icons-material/Layers';
import LinkIcon from '@mui/icons-material/Link';
import NotesIcon from '@mui/icons-material/Notes';
import PersonIcon from '@mui/icons-material/Person';
import ShortTextIcon from '@mui/icons-material/ShortText';
import SignalCellularAltIcon from '@mui/icons-material/SignalCellularAlt';
import TaskAltIcon from '@mui/icons-material/TaskAlt';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import type { EscalationSeverity, IDependency, IEscalation, IIssue, IRisk } from '@pulse/shared';
import {
  Alert,
  Button,
  Chip,
  DateField,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  FormControl,
  ICON_SIZE,
  InputLabel,
  LabelWithIcon,
  Link as MuiLink,
  MarkdownField,
  MenuItem,
  Select,
  Stack,
  TextField,
  Typography,
} from '@pulse/ui';
import { useEffect, useState } from 'react';
import { Link as RouterLink } from 'react-router-dom';

import { AttachmentsPanel } from '../../../components/AttachmentsPanel';
import { CopyLinkButton } from '../../../components/CopyLinkButton';
import { EntityPicker } from '../../../components/EntityPicker';
import { LogEntriesPanel } from '../../../components/LogEntriesPanel';
import { MultiSelectPicker } from '../../../components/MultiSelectPicker';
import { StagedAttachmentsPanel, useStagedAttachments } from '../../../components/StagedAttachmentsPanel';
import { formatDdMmYyyyFromTimestamp } from '../../../lib/date.util';
import { useUserLookup } from '../../access-management/hooks/useUsers';
import { escalationAttachmentDownloadUrl, uploadEscalationAttachment } from '../api/escalation.api';
import { useDependencies } from '../hooks/useDependencies';
import {
  useAddEscalationLogEntry,
  useCreateEscalation,
  useEscalationAttachments,
  useEscalationLog,
  useRemoveEscalationAttachment,
  useTransitionEscalation,
  useUpdateEscalation,
  useUploadEscalationAttachment,
} from '../hooks/useEscalations';
import { useIssues } from '../hooks/useIssues';
import { useEscalationCategoryLookup } from '../hooks/useMasterLookups';
import { useProject } from '../hooks/useProjects';
import { useRisks } from '../hooks/useRisks';
import { useWorkflowDefinition } from '../hooks/useWorkflow';
import { escalationLink } from '../utils/entityLinks';

const SEVERITIES: EscalationSeverity[] = ['Critical', 'High', 'Medium', 'Low'];
// Matches CreateEscalationDto's @MaxLength decorators (apps/api/src/modules/presales/dto/create-escalation.dto.ts).
const TITLE_MAX = 255;
const TEXT_MAX = 1000;

function overLimitHelperText(value: string, max: number, overLimit: boolean): string {
  return overLimit ? `${value.length}/${max} — ${value.length - max} characters over the limit` : `${value.length}/${max}`;
}

// Stable (module-level) empty-array fallbacks — an inline `= []` default is a fresh reference
// on every render while the query is loading, which would make the resync useEffect below
// (which depends on `issues`/`risks`/`dependencies`) fire on every render and loop.
const EMPTY_ISSUES: IIssue[] = [];
const EMPTY_RISKS: IRisk[] = [];
const EMPTY_DEPENDENCIES: IDependency[] = [];

export interface EscalationCreateDefaults {
  title?: string;
  description?: string;
  linkedRiskIds?: string[];
}

interface EscalationFormDialogProps {
  projectId: string;
  open: boolean;
  onClose: () => void;
  escalation: IEscalation | null;
  createDefaults?: EscalationCreateDefaults;
  onSaved: (message: string) => void;
  onError: (message: string) => void;
}

export function EscalationFormDialog({
  projectId,
  open,
  onClose,
  escalation,
  createDefaults,
  onSaved,
  onError,
}: EscalationFormDialogProps) {
  const isEdit = Boolean(escalation);
  const { data: users = [] } = useUserLookup();
  const { data: project } = useProject(projectId);
  const { data: issues = EMPTY_ISSUES } = useIssues(projectId);
  const { data: risks = EMPTY_RISKS } = useRisks(projectId);
  const { data: dependencies = EMPTY_DEPENDENCIES } = useDependencies(projectId);
  // Active-only, with the escalation's own category merged back in so a category selected while
  // active and since deactivated still displays instead of blanking the field.
  const { data: categoryOptions = [] } = useEscalationCategoryLookup();
  const categories =
    escalation?.category && !categoryOptions.some((c) => c.id === escalation.category?.id)
      ? [...categoryOptions, escalation.category]
      : categoryOptions;
  const { data: workflowDefinition } = useWorkflowDefinition('escalation');
  const statusOptions = workflowDefinition?.steps.map((s) => s.name) ?? [];

  const [title, setTitle] = useState(escalation?.title ?? createDefaults?.title ?? '');
  const [description, setDescription] = useState(escalation?.description ?? createDefaults?.description ?? '');
  const [severity, setSeverity] = useState<EscalationSeverity | ''>(escalation?.severity ?? '');
  const [categoryId, setCategoryId] = useState(escalation?.categoryId ?? '');
  const [escalatedToId, setEscalatedToId] = useState(escalation?.escalatedToId ?? '');
  const [ownerId, setOwnerId] = useState(escalation?.ownerId ?? '');
  const [targetResolutionDate, setTargetResolutionDate] = useState(escalation?.targetResolutionDate ?? '');
  // Staged locally like every other field — the Select shows this, not escalation.status
  // directly, so picking a new status doesn't touch the backend until Save (see handleSave).
  const [pendingStatus, setPendingStatus] = useState(escalation?.status ?? '');
  const [resolutionActionTaken, setResolutionActionTaken] = useState(escalation?.resolutionActionTaken ?? '');
  const [linkedIssues, setLinkedIssues] = useState<IIssue[]>(issues.filter((i) => escalation?.linkedIssueIds.includes(i.id)));
  const [linkedRisks, setLinkedRisks] = useState<IRisk[]>(
    risks.filter((r) => (escalation?.linkedRiskIds ?? createDefaults?.linkedRiskIds ?? []).includes(r.id)),
  );
  const [linkedDependencies, setLinkedDependencies] = useState<IDependency[]>(
    dependencies.filter((d) => escalation?.linkedDependencyIds.includes(d.id)),
  );
  const [formError, setFormError] = useState<string | null>(null);
  const {
    staged,
    add: addStaged,
    remove: removeStaged,
    reset: resetStaged,
    uploadAll,
    isUploading: isUploadingStaged,
  } = useStagedAttachments();
  const [targetResolutionDateFieldValid, setTargetResolutionDateFieldValid] = useState(true);

  // The dialog is mounted once and toggled via `open` rather than remounted per edit, so the
  // `useState(escalation?.x ?? ...)` initializers above only ever run for whichever escalation
  // was current on first mount — this re-syncs the form to the escalation actually being
  // edited (or the createDefaults pre-fill / blank for a new one) whenever the target,
  // open-state, defaults, or the linkable-issue/risk/dependency lists change (same pattern as
  // SprintFormDialog.tsx).
  useEffect(() => {
    setTitle(escalation?.title ?? createDefaults?.title ?? '');
    setDescription(escalation?.description ?? createDefaults?.description ?? '');
    setSeverity(escalation?.severity ?? '');
    setCategoryId(escalation?.categoryId ?? '');
    setEscalatedToId(escalation?.escalatedToId ?? '');
    setOwnerId(escalation?.ownerId ?? '');
    setTargetResolutionDate(escalation?.targetResolutionDate ?? '');
    setPendingStatus(escalation?.status ?? '');
    setResolutionActionTaken(escalation?.resolutionActionTaken ?? '');
    setLinkedIssues(issues.filter((i) => escalation?.linkedIssueIds.includes(i.id)));
    setLinkedRisks(risks.filter((r) => (escalation?.linkedRiskIds ?? createDefaults?.linkedRiskIds ?? []).includes(r.id)));
    setLinkedDependencies(dependencies.filter((d) => escalation?.linkedDependencyIds.includes(d.id)));
    setFormError(null);
    setTargetResolutionDateFieldValid(true);
    resetStaged();
  }, [escalation, open, createDefaults, issues, risks, dependencies, resetStaged]);

  const createMutation = useCreateEscalation(projectId);
  const updateMutation = useUpdateEscalation(projectId);
  const transitionMutation = useTransitionEscalation(projectId);
  const { data: logEntries = [], isLoading: logLoading } = useEscalationLog(projectId, escalation?.id ?? '');
  const addLogMutation = useAddEscalationLogEntry(projectId, escalation?.id ?? '');
  const { data: attachments = [], isLoading: attachmentsLoading } = useEscalationAttachments(projectId, escalation?.id ?? '');
  const uploadMutation = useUploadEscalationAttachment(projectId, escalation?.id ?? '');
  const removeAttachmentMutation = useRemoveEscalationAttachment(projectId, escalation?.id ?? '');

  const titleOverLimit = title.length > TITLE_MAX;
  const descriptionOverLimit = description.length > TEXT_MAX;
  const resolutionActionTakenOverLimit = resolutionActionTaken.length > TEXT_MAX;
  const targetResolutionDateBeforeProjectStart = Boolean(
    targetResolutionDate && project?.startDate && targetResolutionDate < project.startDate,
  );
  const isFormValid =
    title.trim().length > 0 && severity !== '' && escalatedToId !== '' && ownerId !== '' &&
    !titleOverLimit && !descriptionOverLimit && !resolutionActionTakenOverLimit && !targetResolutionDateBeforeProjectStart &&
    targetResolutionDateFieldValid;

  function handleSave() {
    const payload = {
      title: title.trim(),
      description: description.trim() || null,
      severity: severity as EscalationSeverity,
      categoryId: categoryId || null,
      escalatedToId,
      ownerId,
      targetResolutionDate: targetResolutionDate || null,
      resolutionActionTaken: resolutionActionTaken.trim() || null,
      linkedIssueIds: linkedIssues.map((i) => i.id),
      linkedRiskIds: linkedRisks.map((r) => r.id),
      linkedDependencyIds: linkedDependencies.map((d) => d.id),
    };
    if (isEdit && escalation) {
      const statusChanged = pendingStatus !== escalation.status;
      if (!statusChanged) {
        updateMutation.mutate(
          { escalationId: escalation.id, data: payload },
          {
            onSuccess: () => { setFormError(null); onSaved('Escalation updated successfully.'); onClose(); },
            onError: (err: Error) => setFormError(err.message),
          },
        );
        return;
      }

      // Status changed (possibly along with other fields) — the workflow transition and the
      // field update are separate, non-transactional backend endpoints, so they're combined into
      // one Save action here rather than fired independently on every field change. Transition
      // fires first: it's the one independently validated against the escalation's
      // WorkflowDefinition, so a rejection there leaves nothing written at all instead of a
      // partially-saved record.
      const original = {
        title: escalation.title,
        description: escalation.description || undefined,
        severity: escalation.severity,
        categoryId: escalation.categoryId || undefined,
        escalatedToId: escalation.escalatedToId,
        ownerId: escalation.ownerId,
        targetResolutionDate: escalation.targetResolutionDate || undefined,
        resolutionActionTaken: escalation.resolutionActionTaken || undefined,
        linkedIssueIds: [...escalation.linkedIssueIds].sort(),
        linkedRiskIds: [...escalation.linkedRiskIds].sort(),
        linkedDependencyIds: [...escalation.linkedDependencyIds].sort(),
      };
      const current = {
        ...payload,
        linkedIssueIds: [...payload.linkedIssueIds].sort(),
        linkedRiskIds: [...payload.linkedRiskIds].sort(),
        linkedDependencyIds: [...payload.linkedDependencyIds].sort(),
      };
      const fieldsChanged = JSON.stringify(current) !== JSON.stringify(original);

      void (async () => {
        try {
          await transitionMutation.mutateAsync({ escalationId: escalation.id, toStep: pendingStatus });

          if (fieldsChanged) {
            try {
              await updateMutation.mutateAsync({ escalationId: escalation.id, data: payload });
            } catch (err) {
              // Transition already committed (and its own cache invalidation already made the
              // table consistent) — report exactly what happened rather than a vague combined
              // message. Re-opening Edit is the correct way to retry just the field changes.
              setFormError(
                `Status changed to "${pendingStatus}" successfully, but the other changes could not be saved: ${err instanceof Error ? err.message : 'unknown error'}.`,
              );
              onClose();
              return;
            }
          }

          setFormError(null);
          onSaved(fieldsChanged ? 'Escalation updated successfully.' : `Escalation moved to "${pendingStatus}".`);
          onClose();
        } catch (err) {
          setFormError(err instanceof Error ? err.message : 'Failed to change status.');
        }
      })();
      return;
    }
    const filesToUpload = staged;
    createMutation.mutate(payload, {
      onSuccess: (created) => {
        void (async () => {
          const uploadOk =
            filesToUpload.length === 0 ||
            (await uploadAll(filesToUpload, (file) => uploadEscalationAttachment(projectId, created.id, file)));
          resetStaged();
          setFormError(null);
          onSaved(
            uploadOk
              ? 'Escalation raised successfully.'
              : 'Escalation raised successfully, but some attachments failed to upload.',
          );
          onClose();
        })();
      },
      onError: (err: Error) => setFormError(err.message),
    });
  }

  const isSaving = createMutation.isPending || updateMutation.isPending || transitionMutation.isPending || isUploadingStaged;

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="md">
      <DialogTitle sx={{ wordBreak: 'break-word' }}>
        {isEdit && escalation ? (
          <MuiLink
            component={RouterLink}
            to={escalationLink(projectId, escalation.escalationKey)}
            onClick={onClose}
            sx={{ wordBreak: 'break-word' }}
          >
            {escalation.escalationKey} — {escalation.title}
          </MuiLink>
        ) : (
          <span>Raise an Escalation</span>
        )}
        {isEdit && escalation && (
          // Inline (not flex) on purpose — nesting inside the title's own text flow lets the
          // buttons ride the end of the last wrapped line for a long title, and drop to a new
          // line only when there's genuinely no room left, instead of the whole icon group
          // jumping below as one atomic flex item the moment the title fills its row.
          <span style={{ marginLeft: 6, verticalAlign: 'middle' }}>
            <CopyLinkButton path={escalationLink(projectId, escalation.escalationKey)} />
          </span>
        )}
      </DialogTitle>
      <DialogContent>
        <Stack spacing={2} sx={{ mt: 1 }}>
          {formError && <Alert severity="error">{formError}</Alert>}

          {isEdit && escalation ? (
            <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap>
              <FormControl size="small" sx={{ minWidth: 200 }}>
                <InputLabel><LabelWithIcon icon={<TrendingUpIcon fontSize={ICON_SIZE.sm} />} label="Status" inline /></InputLabel>
                <Select
                  value={pendingStatus}
                  label={<LabelWithIcon icon={<TrendingUpIcon fontSize={ICON_SIZE.sm} />} label="Status" inline />}
                  onChange={(e) => setPendingStatus(e.target.value)}
                  disabled={isSaving}
                >
                  {statusOptions.map((s) => <MenuItem key={s} value={s}>{s}</MenuItem>)}
                </Select>
              </FormControl>
              {!escalation.isActive && <Chip size="small" label="Inactive" color="default" />}
            </Stack>
          ) : (
            <TextField
              label={<LabelWithIcon icon={<TrendingUpIcon fontSize={ICON_SIZE.sm} />} label="Status" inline />}
              value={workflowDefinition?.steps.find((s) => s.isInitial)?.name ?? statusOptions[0] ?? ''}
              disabled
              size="small"
              sx={{ maxWidth: 200 }}
              helperText="New escalations start in this status"
            />
          )}

          <TextField
            label={<LabelWithIcon icon={<ShortTextIcon fontSize={ICON_SIZE.sm} />} label="Title" inline />}
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            onBlur={() => setTitle((t) => t.trim())}
            required
            fullWidth
            error={titleOverLimit}
            helperText={overLimitHelperText(title, TITLE_MAX, titleOverLimit)}
          />
          <MarkdownField
            label={<LabelWithIcon icon={<NotesIcon fontSize={ICON_SIZE.sm} />} label="Description" inline />}
            value={description}
            onChange={setDescription}
            error={descriptionOverLimit}
            helperText={overLimitHelperText(description, TEXT_MAX, descriptionOverLimit)}
          />

          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
            <FormControl fullWidth required>
              <InputLabel><LabelWithIcon icon={<SignalCellularAltIcon fontSize={ICON_SIZE.sm} />} label="Severity" inline /></InputLabel>
              <Select
                value={severity}
                label={<LabelWithIcon icon={<SignalCellularAltIcon fontSize={ICON_SIZE.sm} />} label="Severity" inline />}
                onChange={(e) => setSeverity(e.target.value as EscalationSeverity)}
              >
                {SEVERITIES.map((s) => <MenuItem key={s} value={s}>{s}</MenuItem>)}
              </Select>
            </FormControl>
            <FormControl fullWidth>
              <InputLabel><LabelWithIcon icon={<LayersIcon fontSize={ICON_SIZE.sm} />} label="Category" inline /></InputLabel>
              <Select
                value={categoryId}
                label={<LabelWithIcon icon={<LayersIcon fontSize={ICON_SIZE.sm} />} label="Category" inline />}
                onChange={(e) => setCategoryId(e.target.value)}
                MenuProps={{ PaperProps: { sx: { maxWidth: { xs: 260, sm: 320, md: 420 }, maxHeight: 380 } } }}
              >
                <MenuItem value="" sx={{ whiteSpace: 'normal', wordBreak: 'break-word' }}><em>None</em></MenuItem>
                {categories.map((c) => (
                  <MenuItem key={c.id} value={c.id} sx={{ whiteSpace: 'normal', wordBreak: 'break-word' }}>{c.name}</MenuItem>
                ))}
              </Select>
            </FormControl>
          </Stack>

          <Stack direction={{ xs: 'column', md: 'row' }} spacing={2}>
            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} sx={{ flex: { md: 2 } }}>
              <EntityPicker
                label="Escalated To"
                labelIcon={<PersonIcon sx={{ fontSize: ICON_SIZE.xs, verticalAlign: 'middle', mr: 0.5, position: 'relative'}} />}
                required
                options={users}
                value={users.find((u) => u.id === escalatedToId) ?? null}
                onChange={(u) => setEscalatedToId(u?.id ?? '')}
              />
              <EntityPicker
                label="Owner"
                labelIcon={<PersonIcon sx={{ fontSize: ICON_SIZE.xs, verticalAlign: 'middle', mr: 0.5, position: 'relative'}} />}
                required
                options={users}
                value={users.find((u) => u.id === ownerId) ?? null}
                onChange={(u) => setOwnerId(u?.id ?? '')}
              />
            </Stack>
            <DateField
              label={<LabelWithIcon icon={<EventIcon fontSize={ICON_SIZE.sm} />} label="Target Resolution Date" inline />}
              value={targetResolutionDate}
              onChange={setTargetResolutionDate}
              onValidityChange={setTargetResolutionDateFieldValid}
              invalidMessage="Please enter a valid Target Resolution Date."
              error={targetResolutionDateBeforeProjectStart}
              helperText={targetResolutionDateBeforeProjectStart ? 'Target Resolution Date cannot be earlier than Project Start Date.' : ' '}
              sx={{ width: { xs: '100%', sm: '48.5%', md: '100%' }, flex: { md: 1 } }}
            />
          </Stack>

          {isEdit && escalation?.actualResolutionDate && (
            <Typography variant="caption" color="text.secondary">
              Actual Resolution Date: {formatDdMmYyyyFromTimestamp(escalation.actualResolutionDate)}
            </Typography>
          )}

          <MarkdownField
            label={<LabelWithIcon icon={<TaskAltIcon fontSize={ICON_SIZE.sm} />} label="Resolution / Action Taken" inline />}
            value={resolutionActionTaken}
            onChange={setResolutionActionTaken}
            error={resolutionActionTakenOverLimit}
            helperText={overLimitHelperText(resolutionActionTaken, TEXT_MAX, resolutionActionTakenOverLimit)}
          />

          <MultiSelectPicker<IIssue>
            label="Link Issues"
            labelIcon={<LinkIcon fontSize={ICON_SIZE.sm} />}
            options={issues}
            value={linkedIssues}
            onChange={setLinkedIssues}
            getOptionKey={(i) => i.id}
            getOptionLabel={(i) => `${i.issueKey} — ${i.name}`}
          />
          <MultiSelectPicker<IRisk>
            label="Link Risks"
            labelIcon={<LinkIcon fontSize={ICON_SIZE.sm} />}
            options={risks}
            value={linkedRisks}
            onChange={setLinkedRisks}
            getOptionKey={(r) => r.id}
            getOptionLabel={(r) => `${r.riskKey} — ${r.title}`}
          />
          <MultiSelectPicker<IDependency>
            label="Link Dependencies"
            labelIcon={<LinkIcon fontSize={ICON_SIZE.sm} />}
            options={dependencies}
            value={linkedDependencies}
            onChange={setLinkedDependencies}
            getOptionKey={(d) => d.id}
            getOptionLabel={(d) => `${d.dependencyKey} — ${d.title}`}
          />

          <Divider />
          <Typography variant="subtitle2"><LabelWithIcon icon={<AttachFileIcon fontSize={ICON_SIZE.sm} />} label="Attachments" inline /></Typography>
          {isEdit && escalation ? (
            <AttachmentsPanel
              attachments={attachments}
              isLoading={attachmentsLoading}
              onUpload={(file) => uploadMutation.mutate(file, { onError: (err: Error) => onError(err.message) })}
              isUploading={uploadMutation.isPending}
              onRemove={(id) =>
                removeAttachmentMutation.mutate(id, {
                  onSuccess: () => onSaved('Attachment deleted successfully.'),
                  onError: (err: Error) => onError(err.message),
                })
              }
              isRemoving={removeAttachmentMutation.isPending}
              downloadUrl={(id) => escalationAttachmentDownloadUrl(projectId, escalation.id, id)}
            />
          ) : (
            <StagedAttachmentsPanel staged={staged} onAdd={addStaged} onRemove={removeStaged} isUploading={isUploadingStaged} />
          )}

          {isEdit && escalation && (
            <>
              <Divider />
              <Typography variant="subtitle2"><LabelWithIcon icon={<HistoryIcon fontSize={ICON_SIZE.sm} />} label="Log" inline /></Typography>
              <LogEntriesPanel
                entries={logEntries}
                isLoading={logLoading}
                onAdd={(note) => addLogMutation.mutate(note, { onError: (err: Error) => onError(err.message) })}
                isAdding={addLogMutation.isPending}
              />
            </>
          )}
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={isSaving}>Cancel</Button>
        <Button variant="contained" onClick={handleSave} disabled={!isFormValid || isSaving}>
          {isSaving ? 'Saving…' : 'Save'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
